// apps/workflow/src/pages/designer/Profile.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Edit,
  Save,
  X,
  Camera,
  RefreshCw,
  Briefcase,
  Clock,
  CheckCircle,
  DollarSign,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

// ─── Helpers ──────────────────────────────────────────────────────
const formatCurrency = (amount) => {
  if (amount == null) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// ─── Main Component ──────────────────────────────────────────────
const DesignerProfile = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    pincode: '',
    bloodGroup: '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [stats, setStats] = useState({
    totalTasks: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    totalEarnings: 0,
  });

  const userRole = user?.role || 'designer';
  const isFreelancer = userRole === 'freelancer';

  // ─── Load initial data ───────────────────────────────────────
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        address: user.address || '',
        pincode: user.pincode || '',
        bloodGroup: user.bloodGroup || '',
      });
      if (user.avatar) {
        setAvatarPreview(pb.files.getUrl(user, user.avatar));
      }
      fetchStats();
    }
  }, [user]);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    try {
      // Get tasks assigned to this user
      const tasks = await pb.collection('project_tasks').getFullList({
        filter: `assignedTo = "${user.id}"`,
        requestKey: null,
      });
      const completed = tasks.filter((t) => t.status === 'completed');
      const inProgress = tasks.filter((t) => t.status === 'in-progress');

      let totalEarnings = 0;
      if (isFreelancer) {
        const earnings = await pb.collection('freelancer_earnings').getFullList({
          filter: `freelancer = "${user.id}" && status = "credited"`,
          requestKey: null,
        });
        totalEarnings = earnings.reduce((sum, e) => sum + (e.amount || 0), 0);
      }

      setStats({
        totalTasks: tasks.length,
        completedTasks: completed.length,
        inProgressTasks: inProgress.length,
        totalEarnings,
      });
    } catch (error) {
      if (!error.isAbort) {
        console.error('Error fetching stats:', error);
      }
    }
  }, [user, isFreelancer]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchStats();
    setRefreshing(false);
    toast.success('Stats refreshed');
  };

  // ─── Edit handlers ──────────────────────────────────────────
  const handleEditToggle = () => {
    if (isEditing) {
      // Cancel edit: reset form data to current user values
      setFormData({
        name: user?.name || '',
        phone: user?.phone || '',
        address: user?.address || '',
        pincode: user?.pincode || '',
        bloodGroup: user?.bloodGroup || '',
      });
      setAvatarFile(null);
      setAvatarPreview(user?.avatar ? pb.files.getUrl(user, user.avatar) : '');
    }
    setIsEditing(!isEditing);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Update user record
      const updateData = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        pincode: formData.pincode,
        bloodGroup: formData.bloodGroup,
      };

      // Update user
      await pb.collection('users').update(user.id, updateData);

      // Update avatar if changed
      if (avatarFile) {
        const form = new FormData();
        form.append('avatar', avatarFile);
        await pb.collection('users').update(user.id, form);
      }

      // Refresh user context
      await refreshUser();

      toast.success('Profile updated successfully');
      setIsEditing(false);
      setAvatarFile(null);
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  // ─── Loading state ──────────────────────────────────────────
  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium">Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <User className="w-6 h-6 text-indigo-600" />
            My Profile
          </h1>
          <p className="text-sm text-slate-500">
            {isFreelancer ? 'Freelancer Profile' : 'Designer Profile'}
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 text-slate-400 hover:text-indigo-600 transition rounded-lg hover:bg-indigo-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Profile Info & Avatar */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Card */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-slate-800">Personal Information</h2>
              <button
                onClick={handleEditToggle}
                className={`p-2 rounded-lg transition ${
                  isEditing
                    ? 'text-red-500 hover:bg-red-50'
                    : 'text-indigo-600 hover:bg-indigo-50'
                }`}
              >
                {isEditing ? <X className="w-5 h-5" /> : <Edit className="w-5 h-5" />}
              </button>
            </div>

            {/* Avatar */}
            <div className="flex items-center gap-4 mb-4">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-slate-100 border-2 border-slate-200 overflow-hidden flex items-center justify-center">
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Avatar"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="w-10 h-10 text-slate-400" />
                  )}
                </div>
                {isEditing && (
                  <label className="absolute bottom-0 right-0 p-1 bg-indigo-600 text-white rounded-full cursor-pointer hover:bg-indigo-700 transition">
                    <Camera className="w-4 h-4" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarChange}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">{user.name}</p>
                <p className="text-xs text-slate-500">{user.email}</p>
                <p className="text-xs text-slate-400 capitalize mt-1">Role: {user.role}</p>
              </div>
            </div>

            {isEditing ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={user.email}
                      disabled
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed"
                    />
                    <p className="text-xs text-slate-400 mt-1">Email cannot be changed</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Blood Group
                    </label>
                    <input
                      type="text"
                      name="bloodGroup"
                      value={formData.bloodGroup}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
                      placeholder="e.g., O+"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Address
                    </label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows="2"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Pincode
                    </label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 outline-none"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleEditToggle}
                    className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition flex items-center gap-2 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" /> {user.name}
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" /> {user.email}
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-400" /> {user.phone || 'Not provided'}
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-slate-400" /> {user.address || 'Not provided'}
                </div>
                {user.pincode && (
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" /> Pincode: {user.pincode}
                  </div>
                )}
                {user.bloodGroup && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">Blood Group:</span> {user.bloodGroup}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Right: Stats & Summary */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Work Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> Total Tasks
                </span>
                <span className="font-bold text-slate-800">{stats.totalTasks}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> In Progress
                </span>
                <span className="font-bold text-amber-600">{stats.inProgressTasks}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Completed
                </span>
                <span className="font-bold text-green-600">{stats.completedTasks}</span>
              </div>
              {isFreelancer && (
                <div className="border-t border-slate-200 pt-3 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-500 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" /> Total Earnings
                    </span>
                    <span className="font-bold text-emerald-600">{formatCurrency(stats.totalEarnings)}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    Credited earnings from completed tasks.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignerProfile;